/**
 * Chart colours for the campaign Overview. Components only use the CSS token
 * references in {@link chartColor}; the hex palettes mirror the `--chart-*`
 * tokens in index.css so tests can check contrast and colour-blind separation
 * in both themes (dark ships, light is ready).
 */

/** Concrete token values per theme, mirrored from index.css. */
export const CHART_PALETTES = {
  dark: {
    surfaces: ['#0A0A0D', '#18181C', '#212126'],
    text: '#F4F4F7',
    accent: '#A88FFF',
    categorical: ['#A88FFF', '#00F5A0', '#FF6B35', '#5CC8FF', '#FF8AC5', '#7E7E86'],
    negative: '#FF6B35',
    positive: '#A88FFF',
    onMark: '#0D0B1E',
    track: '#34343B',
  },
  light: {
    surfaces: ['#FFFFFF', '#F2EEFF'],
    text: '#1A1A22',
    accent: '#6E3FFF',
    categorical: ['#6E3FFF', '#047857', '#C2410C', '#0B6FB8', '#C2307A', '#5A5A70'],
    negative: '#C2410C',
    positive: '#6E3FFF',
    onMark: '#FFFFFF',
    track: '#DDD6F3',
  },
} as const;

/**
 * Card tones: one brand hue family per card (backing, mark, track) so neighbouring
 * cards read apart. Mirrored from the `--chart-tone-*` tokens in index.css; the
 * `tone-*` utilities paint the backing and point `--chart-mark` and `--chart-track`
 * at the family. Heat fills stay on the accent so their text stays readable.
 */
export const CHART_TONES = {
  dark: {
    volt: { backing: '#261E40', mark: '#A88FFF', track: '#30235C' },
    flow: { backing: '#162E29', mark: '#00F5A0', track: '#134939' },
    signal: { backing: '#34221F', mark: '#FF6B35', track: '#542E23' },
  },
  light: {
    volt: { backing: '#F2EEFF', mark: '#6E3FFF', track: '#DDD6F3' },
    flow: { backing: '#E7FBF3', mark: '#047857', track: '#BCEAD6' },
    signal: { backing: '#FFF1EA', mark: '#C2410C', track: '#F8D2BF' },
  },
} as const;

/** A card tone. */
export type ChartTone = keyof typeof CHART_TONES.dark;

/** Tones in rotation order. */
export const TONES: ChartTone[] = ['volt', 'flow', 'signal'];

/** The utility class per tone, written out in full so Tailwind generates each one. */
export const TONE_CLASSES: Record<ChartTone, string> = { volt: 'tone-volt', flow: 'tone-flow', signal: 'tone-signal' };

/** The tone for the card at a position, rotating so neighbours never match. */
export function toneAt(index: number): ChartTone {
  return TONES[index % TONES.length];
}

/** Strongest heat or diverging fill, kept low enough for foreground text to stay readable. */
export const HEAT_MAX = 0.6;

/** Token references for chart marks and chart text. */
export const chartColor = {
  /** The one colour of every single-series chart. */
  accent: 'var(--chart-accent)',
  /** Up to 6 hues, only where colour encodes a category across marks. */
  categorical: ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--chart-6)'],
  negative: 'var(--chart-negative)',
  positive: 'var(--chart-positive)',
  /** Axis lines and the baseline. */
  axis: 'var(--border-color)',
  /** Direct labels and tick text. */
  label: 'var(--muted-foreground)',
  /** Values printed inside heat and diverging cells. */
  text: 'var(--foreground)',
  /** Text printed on a solid mark, such as a stacked segment. */
  onMark: 'var(--chart-on-mark)',
  /** The soft track a bar or column fills. */
  track: 'var(--chart-track)',
  /** The fill of bars, columns and points: the accent, or the card's tone. */
  mark: 'var(--chart-mark)',
} as const;

/** Percent strength of a fill for a 0..1 intensity. */
function strength(intensity: number): number {
  return Math.round(Math.min(1, Math.max(0, intensity)) * HEAT_MAX * 100);
}

/** Sequential fill for a 0..1 intensity: transparent at 0, the accent at the readable maximum. */
export function heatFill(intensity: number): string {
  const percent = strength(intensity);
  return percent ? `color-mix(in srgb, ${chartColor.accent} ${percent}%, transparent)` : 'transparent';
}

/** Diverging fill for a value in -1..1, centred on the surface at 0. */
export function divergingFill(value: number): string {
  const percent = strength(Math.abs(value));
  if (!percent) return 'transparent';
  return `color-mix(in srgb, ${value > 0 ? chartColor.positive : chartColor.negative} ${percent}%, transparent)`;
}
